<template>
  <div class="assistant-cat-home" :style="homeBackgroundStyle">
    <assistant-cat-home-stage
      class="assistant-cat-home__stage"
      :group="roomGroup"
      :writer-type="selectedCatType"
      :cat-name="catName"
      :layout="artworkLayout"
      @tap-burst="onCatTapBurst"
    />
    <div ref="frame" class="assistant-cat-home__frame">
      <header class="assistant-cat-home__header">
        <button
          type="button"
          class="assistant-cat-home__icon-button"
          :aria-label="$t('assistantCatHome.back')"
          @click="onBack"
        >
          <icon-arrow-left />
        </button>
        <button
          type="button"
          class="assistant-cat-home__icon-button"
          :aria-label="$t('assistantCatHome.openMenu')"
          @click="isMenuOpen = !isMenuOpen"
        >
          <icon-hamburger />
        </button>
      </header>

      <assistant-cat-home-speech-bubble
        class="assistant-cat-home__speech"
        :style="speechStyle"
        :title="catName"
        :greeting="greeting"
        :username="displayUsername"
      />
      <assistant-cat-home-action-panel
        class="assistant-cat-home__actions"
        :style="actionsStyle"
        @action="onActionFromPanel"
      />
    </div>

    <assistant-cat-home-menu-sheet
      v-if="isMenuOpen"
      :fish-label="formattedCurrentFish"
      :charge-label="formattedChargeFish"
      :bonus-label="formattedFreeFish"
      :writer-type="selectedCatType"
      :feedback-manuscript-mode="feedbackManuscriptMode"
      :generate-summary-mode="generateSummaryMode"
      @close="isMenuOpen = false"
      @action="onActionFromSheet"
      @charge="onChargeFromSheet"
      @charge-breakdown="onChargeBreakdownFromSheet"
    />

    <transition name="assistant-cat-home-loading">
      <assistant-cat-home-loading
        v-if="showLoadingOverlay"
        :paw-count="pawCount"
        :show-retry="loadError"
        @retry="onRetryLoad"
      />
    </transition>

    <assistant-cat-selection-dialog
      v-if="showAssistantCatDialog"
      :assistant-cat-master="assistantCatMaster"
      @close="onCloseAssistantCatDialog"
    />
    <cat-nickname-input-dialog
      v-if="showNicknameInputDialog"
      :assistant-cat="selectedAssistantCat"
      :initial-nickname="inputDialogInitialNickname"
      @save="onSaveNickname"
      @cancel="onCancelNicknameInput"
    />
    <cat-nickname-confirm-dialog
      v-if="showNicknameConfirmDialog"
      :assistant-cat="selectedAssistantCat"
      :nickname="pendingNickname"
      :is-resetting-to-default="isResettingNicknameToDefault"
      @confirm="onConfirmNickname"
      @close="onCloseNicknameConfirm"
    />
    <cat-nickname-success-dialog
      v-if="showNicknameSuccessDialog"
      :assistant-cat="selectedAssistantCat"
      :reaction-message="nicknameReactionMessage"
      @ok="onOkNicknameSuccess"
    />
    <feedback-manuscript-mode-selection-dialog
      v-if="showFeedbackManuscriptModeDialog"
      :assistant-cat-master="assistantCatMaster"
      @close="onCloseFeedbackManuscriptModeDialog"
    />
    <generate-summary-mode-selection-dialog
      v-if="showGenerateSummaryModeDialog"
      :assistant-cat-master="assistantCatMaster"
      @close="onCloseGenerateSummaryModeDialog"
    />
    <assistant-cat-home-request-dialog
      v-if="showRequestDialog && requestAction"
      :action="requestAction"
      :writer-type="selectedCatType"
      @close="onCloseRequestDialog"
    />
    <assistant-cat-home-charge-breakdown-dialog
      v-if="showChargeBreakdownDialog"
      :web-charge-label="formattedPrepaidFish"
      @close="showChargeBreakdownDialog = false"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import AssistantCatHomeActionPanel from "@/components/organisms/assistantCatHome/AssistantCatHomeActionPanel.vue";
import AssistantCatHomeLoading from "@/components/organisms/assistantCatHome/AssistantCatHomeLoading.vue";
import AssistantCatHomeMenuSheet from "@/components/organisms/assistantCatHome/AssistantCatHomeMenuSheet.vue";
import AssistantCatHomeSpeechBubble from "@/components/organisms/assistantCatHome/AssistantCatHomeSpeechBubble.vue";
import AssistantCatHomeStage from "@/components/organisms/assistantCatHome/AssistantCatHomeStage.vue";
import AssistantCatSelectionDialog from "@/components/ui/AssistantCatSelectionDialog.vue";
import CatNicknameConfirmDialog from "@/components/ui/CatNicknameConfirmDialog.vue";
import CatNicknameInputDialog from "@/components/ui/CatNicknameInputDialog.vue";
import CatNicknameSuccessDialog from "@/components/ui/CatNicknameSuccessDialog.vue";
import FeedbackManuscriptModeSelectionDialog from "@/components/ui/FeedbackManuscriptModeSelectionDialog.vue";
import AssistantCatHomeChargeBreakdownDialog from "@/components/ui/AssistantCatHomeChargeBreakdownDialog.vue";
import AssistantCatHomeRequestDialog from "@/components/ui/AssistantCatHomeRequestDialog.vue";
import GenerateSummaryModeSelectionDialog from "@/components/ui/GenerateSummaryModeSelectionDialog.vue";
import {
  AssistantCatHomeAction,
  AssistantCatHomeUiSurface,
  assistantCatGroupFromType,
  assistantCatHomeRoomFill,
  assistantCatHomeRoomSrc,
  assistantCatWholeBodyImageUrl,
  isWorkSelectionAction,
  keepsAssistantCatHomeSheetOpen,
  randomAssistantCatWriterType,
} from "@/constants/assistantCatHome";
import { assistantCatHomeFunctionType } from "@/constants/assistantCatHomeRequest";
import { assistantCatHomeMenuItemId } from "@/constants/assistantCatHomeAnalytics";
import { User } from "@/lib/models";
import {
  ASSISTANT_CAT_HOME_FIGMA_H,
  ASSISTANT_CAT_HOME_FIGMA_W,
  AssistantCatHomeLayout,
  resolveAssistantCatHomeLayout,
} from "@/constants/assistantCatHomeLayout";
import { AssistantCatHomeLoadingProgress } from "@/constants/assistantCatHomeLoadingProgress";
import { AssistantCat, AssistantCatMaster } from "@/lib/models/assistantCat";
import { NolaAnalytics, NolaContentType, NolaItemId } from "@/lib/utils/analytics";
import { logError } from "@/lib/utils/logger";
import IconArrowLeft from "~icons/figma/ArrowLeft";
import IconHamburger from "~icons/figma/Hamburger";

export default Vue.extend({
  name: "AssistantCatHomePage",
  components: {
    IconArrowLeft,
    IconHamburger,
    AssistantCatHomeActionPanel,
    AssistantCatHomeLoading,
    AssistantCatHomeMenuSheet,
    AssistantCatHomeSpeechBubble,
    AssistantCatHomeStage,
    AssistantCatSelectionDialog,
    CatNicknameConfirmDialog,
    CatNicknameInputDialog,
    CatNicknameSuccessDialog,
    FeedbackManuscriptModeSelectionDialog,
    AssistantCatHomeRequestDialog,
    AssistantCatHomeChargeBreakdownDialog,
    GenerateSummaryModeSelectionDialog,
  },
  data() {
    return {
      showLoadingOverlay: true,
      pawCount: 0,
      loadError: false,
      loadingProgress: new AssistantCatHomeLoadingProgress(),
      loadingTickTimer: null as number | null,
      loadingTimeoutTimer: null as number | null,
      isMenuOpen: false,
      assistantCatMaster: null as AssistantCatMaster | null,
      fallbackCatType: randomAssistantCatWriterType(),
      assistantCats: [] as AssistantCat[],
      showAssistantCatDialog: false,
      showNicknameInputDialog: false,
      showNicknameConfirmDialog: false,
      showNicknameSuccessDialog: false,
      showFeedbackManuscriptModeDialog: false,
      showGenerateSummaryModeDialog: false,
      showRequestDialog: false,
      showChargeBreakdownDialog: false,
      requestAction: null as AssistantCatHomeAction | null,
      pendingNickname: "",
      nicknameReactionMessage: "",
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      frameWidth: ASSISTANT_CAT_HOME_FIGMA_W,
      frameHeight: ASSISTANT_CAT_HOME_FIGMA_H,
      pageWidth: ASSISTANT_CAT_HOME_FIGMA_W,
      pageHeight: ASSISTANT_CAT_HOME_FIGMA_H,
      frameResizeObserver: null as ResizeObserver | null,
    };
  },
  computed: {
    ...mapGetters("fishModule", [
      "formattedCurrentFish",
      "formattedChargeFish",
      "formattedFreeFish",
      "formattedPrepaidFish",
    ]),
    user(): User | null {
      return this.$store.getters["userModule/user"] || null;
    },
    hasWriterTypeResult(): boolean {
      return this.assistantCatMaster !== null;
    },
    selectedCatType(): string | null {
      return this.assistantCatMaster?.selectedType || this.fallbackCatType;
    },
    feedbackManuscriptMode(): string {
      return this.assistantCatMaster?.feedbackManuscriptMode || "NORMAL";
    },
    generateSummaryMode(): string {
      return this.assistantCatMaster?.generateSummaryMode || "WAKUWAKU";
    },
    selectedAssistantCat(): AssistantCat | null {
      return this.assistantCats.find((cat) => cat.selected) || null;
    },
    catName(): string {
      const name = this.selectedAssistantCat?.name?.trim() || "";
      if (name) return name;
      const officialName = this.selectedAssistantCat?.officialName?.trim() || "";
      if (officialName) return officialName;
      return this.$t("assistantCatHome.pageTitle") as string;
    },
    displayUsername(): string {
      return this.user?.name || (this.$t("assistantCatHome.usernameFallback") as string);
    },
    roomGroup() {
      return assistantCatGroupFromType(this.selectedCatType);
    },
    roomFillColor(): string {
      return assistantCatHomeRoomFill(this.roomGroup);
    },
    homeBackgroundStyle(): { backgroundColor?: string } {
      return { backgroundColor: this.roomFillColor };
    },
    greeting(): string {
      const username = this.displayUsername;
      const type = (this.selectedCatType || "").toLowerCase();
      const key = type ? `assistantCatHome.greetingByWriterType.${type}` : "assistantCatHome.greetingFallback";
      if (this.$te(key)) {
        return this.$t(key, { username }) as string;
      }
      return this.$t("assistantCatHome.greetingFallback", { username }) as string;
    },
    inputDialogInitialNickname(): string {
      return this.pendingNickname || this.selectedAssistantCat?.name || "";
    },
    isResettingNicknameToDefault(): boolean {
      return this.pendingNickname === "";
    },
    homeLayout(): AssistantCatHomeLayout {
      return resolveAssistantCatHomeLayout({
        width: this.frameWidth,
        height: this.frameHeight,
      });
    },
    artworkLayout(): AssistantCatHomeLayout {
      return resolveAssistantCatHomeLayout({
        width: this.pageWidth,
        height: this.pageHeight,
      });
    },
    speechStyle(): Record<string, string> {
      return {
        left: `${this.homeLayout.bubbleLeft}px`,
        top: `${this.homeLayout.bubbleTop}px`,
        width: `${this.homeLayout.bubbleW}px`,
      };
    },
    actionsStyle(): Record<string, string> {
      return {
        left: `${this.homeLayout.bubbleLeft}px`,
        width: `${this.homeLayout.bubbleW}px`,
        bottom: `${this.homeLayout.actionBottom}px`,
      };
    },
  },
  created() {
    this.startLoadingGate();
    this.load();
  },
  mounted() {
    this.observeFrameSize();
  },
  beforeDestroy() {
    this.clearLoadingTimers();
    this.teardownFrameObserver();
  },
  methods: {
    observeFrameSize() {
      const frame = this.$refs.frame as HTMLElement | undefined;
      if (!frame || typeof ResizeObserver === "undefined") {
        return;
      }
      this.syncFrameSize();
      this.frameResizeObserver = new ResizeObserver(() => {
        this.syncFrameSize();
      });
      this.frameResizeObserver.observe(frame);
      if (this.$el instanceof HTMLElement) {
        this.frameResizeObserver.observe(this.$el);
      }
    },
    syncFrameSize() {
      const frame = this.$refs.frame as HTMLElement | undefined;
      if (frame) {
        const rect = frame.getBoundingClientRect();
        this.frameWidth = rect.width;
        this.frameHeight = rect.height;
      }
      if (this.$el instanceof HTMLElement) {
        const page = this.$el.getBoundingClientRect();
        this.pageWidth = page.width;
        this.pageHeight = page.height;
      }
    },
    teardownFrameObserver() {
      if (this.frameResizeObserver) {
        this.frameResizeObserver.disconnect();
        this.frameResizeObserver = null;
      }
    },
    startLoadingGate() {
      this.clearLoadingTimers();
      this.showLoadingOverlay = true;
      this.pawCount = this.loadingProgress.pawCount;
      this.loadingTickTimer = window.setInterval(() => {
        const done = this.loadingProgress.onTick();
        this.pawCount = this.loadingProgress.pawCount;
        if (done) {
          this.beginFadeOut();
        }
      }, this.loadingProgress.stepIntervalMs);
      this.loadingTimeoutTimer = window.setTimeout(() => {
        this.forceFinishLoading();
      }, 5000);
    },
    clearLoadingTimers() {
      if (this.loadingTickTimer !== null) {
        window.clearInterval(this.loadingTickTimer);
        this.loadingTickTimer = null;
      }
      if (this.loadingTimeoutTimer !== null) {
        window.clearTimeout(this.loadingTimeoutTimer);
        this.loadingTimeoutTimer = null;
      }
    },
    beginFadeOut() {
      if (!this.showLoadingOverlay) {
        return;
      }
      this.clearLoadingTimers();
      this.showLoadingOverlay = false;
    },
    forceFinishLoading() {
      if (this.loadingProgress.finished || this.loadError) {
        return;
      }
      this.loadingProgress.forceFinish();
      this.beginFadeOut();
    },
    preloadImage(src: string): Promise<void> {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve();
        image.onerror = () => resolve();
        image.src = src;
      });
    },
    async precacheHomeImages() {
      const sources = [assistantCatHomeRoomSrc(this.roomGroup)];
      const catSrc = assistantCatWholeBodyImageUrl(this.selectedCatType);
      if (catSrc) {
        sources.push(catSrc);
      }
      await Promise.race([
        Promise.all(sources.map((src) => this.preloadImage(src))),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 3000);
        }),
      ]);
    },
    async load() {
      this.loadError = false;
      try {
        await Promise.all([
          this.$store.dispatch("userModule/initialize"),
          this.$store.dispatch("fishModule/fetchFishData"),
          this.fetchAssistantCatMaster(),
        ]);
        if (this.hasWriterTypeResult) {
          await this.fetchAssistantCats();
        }
        this.loadingProgress.markIdentityReady();
        await this.precacheHomeImages();
        this.loadingProgress.markImagesReady();
        if (this.loadingProgress.finished) {
          this.beginFadeOut();
        }
      } catch (error) {
        this.loadError = true;
        this.clearLoadingTimers();
        logError({ module: "AssistantCatHomePage", action: "load" }, "アシスタント猫ホームの読み込みに失敗", error);
      }
    },
    onRetryLoad() {
      this.loadingProgress.reset();
      this.pawCount = 0;
      this.loadError = false;
      this.startLoadingGate();
      this.load();
    },
    async fetchAssistantCatMaster() {
      this.assistantCatMaster = await this.$store.dispatch("assistantCatModule/fetchAssistantCatMaster");
    },
    async fetchAssistantCats() {
      this.assistantCats = await this.$store.dispatch("assistantCatModule/fetchAssistantCatList");
    },
    onBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push({ name: "home" });
    },
    onClickFish() {
      this.$router.push({ name: "purchaseFish" });
    },
    onChargeFromSheet() {
      this.logHomeTap(NolaItemId.AssistantCatHomeMenuCharge, "sheet");
      this.onClickFish();
    },
    onChargeBreakdownFromSheet() {
      this.logHomeTap(NolaItemId.AssistantCatHomeMenuCharge, "sheet");
      this.showChargeBreakdownDialog = true;
    },
    onActionFromPanel(action: AssistantCatHomeAction) {
      this.onAction(action, "action_panel");
    },
    onActionFromSheet(action: AssistantCatHomeAction) {
      this.onAction(action, "sheet");
    },
    logHomeTap(itemId: NolaItemId, uiSurface: AssistantCatHomeUiSurface, functionType?: string | null) {
      const params: Record<string, string | number> = {
        content_type: NolaContentType.Button,
        item_id: itemId,
        ui_surface: uiSurface,
      };
      if (functionType) {
        params.function_type = functionType;
      }
      this.nolaAnalytics.logCustomEvent("select_content", params);
    },
    async onAction(action: AssistantCatHomeAction, uiSurface: AssistantCatHomeUiSurface = "sheet") {
      this.logHomeTap(assistantCatHomeMenuItemId(action), uiSurface, assistantCatHomeFunctionType(action));
      if (!keepsAssistantCatHomeSheetOpen(action)) {
        this.isMenuOpen = false;
      }
      if (isWorkSelectionAction(action)) {
        this.requestAction = action;
        this.showRequestDialog = true;
        return;
      }
      if (action === "changeAssistantCat") {
        this.showAssistantCatDialog = true;
        return;
      }
      if (action === "changeNickname") {
        this.pendingNickname = "";
        this.showNicknameInputDialog = true;
        return;
      }
      if (action === "changeFeedbackMode") {
        this.showFeedbackManuscriptModeDialog = true;
        return;
      }
      if (action === "changeSummaryMode") {
        this.showGenerateSummaryModeDialog = true;
        return;
      }
      if (action === "fishHistory") {
        this.$router.push({ name: "fishHistory" });
        return;
      }
      if (action === "paymentManage") {
        this.$router.push({ name: "paymentManagement" });
      }
    },
    onCatTapBurst(tapCount: number) {
      if (typeof tapCount !== "number" || tapCount <= 0) {
        return;
      }
      this.nolaAnalytics.logCustomEvent("select_content", {
        content_type: NolaContentType.Button,
        item_id: NolaItemId.AssistantCatHomeCatTap,
        ui_surface: "room",
        tap_count: tapCount,
      });
    },
    onCloseRequestDialog() {
      this.showRequestDialog = false;
      this.requestAction = null;
    },
    async onCloseAssistantCatDialog() {
      this.showAssistantCatDialog = false;
      if (this.hasWriterTypeResult) {
        await this.fetchAssistantCats();
        await this.fetchAssistantCatMaster();
      }
    },
    onSaveNickname(nickname: string) {
      this.pendingNickname = nickname;
      this.showNicknameInputDialog = false;
      this.showNicknameConfirmDialog = true;
    },
    onCancelNicknameInput() {
      this.showNicknameInputDialog = false;
    },
    async onConfirmNickname() {
      const catType = this.selectedAssistantCat?.assistantCatType;
      if (!catType) {
        return;
      }
      try {
        await this.$store.dispatch("assistantCatModule/updateCatNickname", {
          assistantCatType: catType,
          name: this.pendingNickname,
        });
        await this.fetchAssistantCats();
        this.nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatChangeNickname, catType);
        this.nicknameReactionMessage = this.selectedAssistantCat?.reactionAfterSetName || "";
        this.showNicknameConfirmDialog = false;
        this.showNicknameSuccessDialog = true;
      } catch (error) {
        logError({ module: "AssistantCatHomePage", action: "onConfirmNickname" }, "ニックネーム変更に失敗", error);
        this.showNicknameSuccessDialog = false;
        this.showNicknameConfirmDialog = true;
        this.$notify({
          title: this.$t("myPage.assistantCat.nicknameChangeError") as string,
          type: "error",
        });
      }
    },
    onCloseNicknameConfirm() {
      this.showNicknameConfirmDialog = false;
    },
    onOkNicknameSuccess() {
      this.showNicknameSuccessDialog = false;
      this.pendingNickname = "";
      this.nicknameReactionMessage = "";
    },
    async onCloseFeedbackManuscriptModeDialog() {
      this.showFeedbackManuscriptModeDialog = false;
      await this.fetchAssistantCatMaster();
    },
    async onCloseGenerateSummaryModeDialog() {
      this.showGenerateSummaryModeDialog = false;
      await this.fetchAssistantCatMaster();
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.assistant-cat-home {
  --home-frame-max: 590px;
  --home-pad-x: 16px;
  --home-header-top: 8px;
  --home-header-button: 40px;

  position: relative;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  height: 100vh;
  overflow-x: hidden;
  background: var(--color-background-normal);
  color: var(--color-text-normal);

  @supports (height: 100dvh) {
    height: 100dvh;
  }
}

.assistant-cat-home__frame {
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  max-width: var(--home-frame-max);
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.assistant-cat-home-loading-leave-active {
  transition: opacity 280ms ease-out;
}

.assistant-cat-home-loading-leave-to {
  opacity: 0;
}

.assistant-cat-home__header {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 5;
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: var(--home-header-top) var(--home-pad-x) 0;
}

.assistant-cat-home__icon-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--home-header-button);
  height: var(--home-header-button);
  padding: 0;
  border: none;
  border-radius: var(--radius-circle);
  background: var(--color-background-normal);
  box-shadow: var(--shadow-md);
  color: var(--color-text-normal);
  font-size: 20px;
  line-height: 0;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring), var(--shadow-md);
  }

  ::v-deep .figma-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
}

.assistant-cat-home__stage {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.assistant-cat-home__speech {
  position: absolute;
  z-index: 3;
  pointer-events: auto;
}

.assistant-cat-home__actions {
  position: absolute;
  z-index: 4;
  box-sizing: border-box;
  min-width: 0;
  pointer-events: auto;
}
</style>
